// Runtime Topology Report — Fase 14.1
import { MODULES, STORES, ENGINES, CHANNELS, ROUTES, type Domain } from './architectureManifest'
import { getChannelSnapshot }    from '@/core/leak/channelMonitor'
import { getLastLeakReport }     from '@/core/leak/memoryLeakDetector'
import { getLastHealthSnapshot } from '@/core/runtimeHealthMonitor'

export interface OrphanReport { type: 'module' | 'store' | 'route' | 'channel'; id: string; path: string; reason: string }

export interface TopologyReport {
  timestamp: string; activeChannels: number; zombieChannels: number
  openChannelNames: string[]; leakRisk: string; memoryMB: number | null
  overallHealth: string; storesWithoutReset: string[]
  engineDomains: Record<Domain, string[]>; potentialOrphans: OrphanReport[]; summary: string
}

export function runtimeTopologyReport(): TopologyReport {
  const now        = new Date().toISOString()
  const chanSnap   = getChannelSnapshot()
  const openNames  = chanSnap.openChannels.map((c) => c.name)
  const zombieCount = chanSnap.zombies.length
  const leakReport = getLastLeakReport()
  const health     = getLastHealthSnapshot()
  const leakRisk   = leakReport?.overallRisk ?? 'no data'
  const memoryMB   = health?.memory.heapUsedMB ?? null
  const overallHealth = health?.overall ?? 'no data'
  const storesWithoutReset = STORES.filter((s) => !s.hasReset).map((s) => s.id)

  const engineDomains = {} as Record<Domain, string[]>
  for (const eng of ENGINES) {
    if (!engineDomains[eng.domain]) engineDomains[eng.domain] = []
    engineDomains[eng.domain].push(eng.id)
  }

  const orphans: OrphanReport[] = []
  for (const mod of MODULES) {
    if (mod.hasCleanup && !ENGINES.find((e) => e.path === mod.path)) {
      orphans.push({ type: 'module', id: mod.id, path: mod.path, reason: 'hasCleanup=true but no EngineDescriptor' })
    }
  }
  for (const store of STORES) {
    if (store.isTenantScoped && !store.hasReset) {
      orphans.push({ type: 'store', id: store.id, path: store.path, reason: 'Tenant-scoped store missing reset()' })
    }
  }
  const enterpriseRoutes = ['/twin', '/vision/cameras', '/executive', '/executive/war-room']
  for (const route of ROUTES) {
    if (route.requiredRole && !enterpriseRoutes.includes(route.path)) {
      orphans.push({ type: 'route', id: route.path, path: route.pageFile, reason: `requiredRole="${route.requiredRole}" not in EnterpriseRoute list — verify App.tsx` })
    }
  }

  const summaryParts = [
    `${chanSnap.openChannels.length} channels`,
    zombieCount > 0 ? `${zombieCount} zombies` : 'no zombies',
    memoryMB !== null ? `${memoryMB.toFixed(0)}MB` : 'heap N/A',
    `health:${overallHealth}`,
    orphans.length > 0 ? `${orphans.length} orphan(s)` : 'no orphans',
  ]

  return {
    timestamp: now, activeChannels: chanSnap.openChannels.length, zombieChannels: zombieCount,
    openChannelNames: openNames, leakRisk, memoryMB, overallHealth, storesWithoutReset,
    engineDomains, potentialOrphans: orphans, summary: summaryParts.join(' | '),
  }
}

export function printTopologyReport(report: TopologyReport): void {
  console.groupCollapsed('[Canteiro] Topology Report — ' + report.timestamp)
  console.log(report.summary)
  if (report.potentialOrphans.length > 0) {
    console.group('Potential Orphans')
    report.potentialOrphans.forEach((o) => console.warn(`[${o.type}] ${o.id}: ${o.reason}`))
    console.groupEnd()
  }
  console.groupEnd()
}
